import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, MessageCircle } from "lucide-react";
import { SITE, NAV } from "@/data/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState("anasayfa");
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track active section on home page
  useEffect(() => {
    if (!onHome) return;
    const ids = NAV.map(n => n.id);
    const onScroll = () => {
      let current = "anasayfa";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 120) current = id;
      }
      setActiveId(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onHome]);

  const handleNav = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-slate-200" : "bg-white border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 sm:gap-4 group">
            <img
              src="/logo.png?v=2"
              alt="DATAVIB - Makine Bakım Teknolojileri"
              className="h-14 sm:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.04]"
              loading="eager"
            />
            <span className="sr-only">DATAVIB</span>
            <span
              className="hidden md:inline-block border-l border-slate-200 pl-3 sm:pl-4 text-[10px] lg:text-[11px] font-mono-accent font-semibold uppercase tracking-[0.22em] text-slate-600 leading-snug max-w-[140px]"
              aria-hidden="true"
            >
              Makine Bakım<br />Teknolojileri
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => handleNav(n.id)}
                data-testid={`nav-${n.id}`}
                className={`nav-link text-sm font-medium ${
                  onHome && activeId === n.id ? "text-cyan-600 active" : "text-slate-700 hover:text-slate-900"
                }`}
              >
                {n.label}
              </button>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <button
              onClick={() => handleNav("iletisim")}
              data-testid="header-quote-btn"
              className="btn-primary text-sm"
            >
              Teklif Al <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-slate-900"
            aria-label="Menüyü aç/kapat"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => handleNav(n.id)}
                data-testid={`mobile-nav-${n.id}`}
                className="text-left py-3 px-2 text-slate-800 font-medium border-b border-slate-100 hover:text-cyan-600"
              >
                {n.label}
              </button>
            ))}
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp mt-3"
              data-testid="mobile-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp'tan Yaz
            </a>
            <button
              onClick={() => handleNav("iletisim")}
              data-testid="mobile-quote-btn"
              className="btn-primary mt-2"
            >
              Teklif Al <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
